import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeCampaignResponse, IMicrositeCampaignValidatedMailingListResponse } from '@nkr/store/index';
import { IBaseResponse, IMicrositeCampaignValidatedMailingList } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormGroup, Panel, Row, Well } from 'react-bootstrap';
import CampaignSteps from './campaign-steps';
import { AgGridColumnProps, AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-community';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

const columnMap = [
    'First Name',
    'Last Name',
    'Address Line 1',
    'Address Line 2',
    'City',
    'State',
    'Postal Code'
];

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import { GridWrapper } from './styles';

interface IDataRow extends IMicrositeCampaignValidatedMailingList {
    isErrorRow: boolean
}

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    getValidatedCampaignMailingList: (id: number) => Promise<IMicrositeCampaignValidatedMailingListResponse>,
    confirmMailingList: (id: number) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    id: number,
    campaignName: string,
    startDate: string,
    endDate: string,
    processedByNkr: boolean,
    hasColumnHeaders: boolean,
    mailingListValidated: boolean,
    columns: string[],
    data: IDataRow[]
}

class ConfirmMailingList extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        campaignName: '',
        startDate: '',
        endDate: '',
        processedByNkr: true,
        hasColumnHeaders: false,
        mailingListValidated: false,
        columns: [],
        data: []
    }

    gridApi: GridApi;
    columnApi: ColumnApi;

    onGridReady = (params) => {
        this.gridApi = params.api;
        this.columnApi = params.columnApi;

        this.getData();
    }

    onFirstDataRendered = () => {
        this.gridApi.sizeColumnsToFit();
    };

    componentDidMount = () => {

    }

    handleSubmit = (continueNextStep: boolean) => {
        this.props.showLoading('Confirming mailing list, one moment please...').then(() => {
            this.props.confirmMailingList(this.state.id)
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Mailing list confirmation saved successfully, going to the signature upload step...', {
                            onClose: () => {
                                window.location.href = continueNextStep ? this.getNextUrl() : this.getReturnUrl();
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                    toast.error('Unable to save the mailing list confirmation, the following error occurred: ' + error.message);
                });
        });
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    getNextUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaign/${this.state.id}/upload-signature-file` : `/center/${this.props.centerId}/microsite/campaign/${this.state.id}/upload-signature-file`;
        return href;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    getColumns = () => {
        if (this.state.columns.length > 0) {
            let columns: AgGridColumnProps[] = this.state.columns.map((column, index) => {
                return {
                    colId: index.toString(),
                    headerName: column,
                    headerClass: 'data-header',
                    cellClass: 'data-cell',
                    suppressMenu: true,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        if (!props.data.isErrorRow) {
                            return <>{props.data.columns[index]}</>;
                        } else {
                            return <div className='text-left text-danger'>Missing data for: {props.data.errors.join(', ')}</div>;
                        }
                    },
                    colSpan: (params: any) => {
                        if (params.data.isErrorRow) {
                            return this.state.columns.length + 1;
                        }

                        return 1;
                    }
                };
            });

            // columns.unshift({
            //     colId: '-1',
            //     headerClass: 'data-header',
            //     cellClass: 'data-cell',
            //     // field: 'id',
            //     cellRenderer: 'agGroupCellRenderer',
            //     width: 50,
            //     suppressMenu: true,
            //     valueGetter: (props: ValueGetterParams) => 'E'
            // });

            columns.push({
                colId: '7',
                headerName: 'Status',
                headerClass: 'data-header',
                cellClass: 'data-cell',
                suppressMenu: true,
                cellRendererFramework: (props: ICellRendererParams) => {
                    return <>{props.data.errors.length > 0 ? <span className='text-danger' style={{ fontWeight: 600 }}>Invalid</span> : <span className='text-success'>Valid</span>}</>;
                }
            });

            return columns;
        } else {
            return [];
        }
    }

    getData = () => {
        this.props.showLoading('Getting the campaign, one moment please...').then(() => {
            Promise.all([
                this.props.getCampaign(this.props.campaignId),
                this.props.getValidatedCampaignMailingList(this.props.campaignId)
            ])
                .then(([campaignResponse, mailingListResponse]) => {
                    if (campaignResponse.data.success && mailingListResponse.data.success) {
                        let { id, campaignName, startDate, endDate, processedByNkr, mailingListHasColumnHeaders: hasColumnHeaders, mailingListValidated, columns } = campaignResponse.data.results

                        let data: IDataRow[] = [];

                        mailingListResponse.data.results.forEach(item => {
                            data.push({
                                ...item,
                                isErrorRow: false
                            });

                            if (item.errors.length > 0) {
                                data.push({
                                    ...item,
                                    isErrorRow: true
                                });
                            }
                        });

                        this.setState({
                            id,
                            campaignName,
                            startDate,
                            endDate,
                            processedByNkr,
                            hasColumnHeaders,
                            mailingListValidated,
                            columns,
                            data
                        }, () => {
                            this.gridApi.refreshCells();
                        });
                    }

                    this.props.hideLoading();
                });
        });
    }

    isValid = () => {
        return this.state.data.filter(x => x.errors.length > 0).length == 0;
    }

    render = () => {
        const isValid = this.isValid();
        const dataColumns = this.getColumns();

        const detailCellRendererParams = {
            detailGridOptions: {
                columnDefs: [
                    {
                        cellRendererFramework: (props: ICellRendererParams) => {
                            return (
                                <span>{props.data}</span>
                            )
                        }
                    },
                ],
                defaultColDef: { flex: 1 },
                headerHeight: 0
            },
            getDetailRowData: (params) => {
                params.successCallback(params.data.errors);
            },
            template:
                '<div style="height: 100%; background-color: #edf6ff; padding: 20px; box-sizing: border-box;">' +
                '  <div style="height: 15%; padding: 2px; font-weight: bold;">###### Data missing for the following</div>' +
                '  <div ref="eDetailGrid" style="height: 50%;"></div>' +
                '</div>',
        };

        const gridHeight = (this.state.data.length * 30) < 400 ? (this.state.data.length * 30) + 31 : 391;

        return (
            <>
                <Row>
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 25 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>Campaign Mailing List Mapping</h1>
                            <div className='pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>

                        {this.state.mailingListValidated && (
                            <Alert bsStyle='success'>
                                The mailing list has already been confirmed.
                            </Alert>
                        )}

                        <Alert bsStyle='info'>
                            <div style={{ marginBottom: 5 }}>For a mailing list to be valid, the "Status" column needs to display "Valid" for each record in the file.</div>
                            <div>There are two possible reasons for the status to be "Invalid":</div>
                            <ul>
                                <li>One of the following columns is missing data: First Name, Last Name, Address Line 1, City, State, Postal Code</li>
                                <li>The entire row is empty</li>
                            </ul>
                        </Alert>

                        <Well>
                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign:</Col>
                                <Col xs={12} lg={8}>{this.state.campaignName}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign Type:</Col>
                                <Col xs={12} lg={8}>Mailed Out</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>NKR will process the mailing:</Col>
                                <Col xs={12} lg={8}>{this.state.processedByNkr ? 'Yes' : 'No'}</Col>
                            </Row>

                            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Does the first row contain column headers?</Col>
                                <Col xs={12} lg={8}>{this.state.hasColumnHeaders ? 'Yes' : 'No'}</Col>
                            </Row>
                        </Well>

                        <GridWrapper className='ag-theme-bootstrap' style={{ height: gridHeight + 'px' }}>
                            <AgGridReact
                                onGridReady={this.onGridReady}
                                headerHeight={30}
                                rowHeight={30}
                                rowData={this.state.data}
                                suppressCellSelection={true}
                                suppressContextMenu={true}
                                columnDefs={dataColumns}
                                onFirstDataRendered={this.onFirstDataRendered}
                            />
                        </GridWrapper>
                        <div style={{ marginBottom: 20 }}>{this.state.data.length} rows</div>

                        <Well bsSize='sm'>
                            <CampaignSteps currentStep={3} campaignId={this.props.campaignId} />
                        </Well>

                        <FormGroup>
                            <div className='col-xs-12 col-lg-12 text-center'>
                                {!this.state.mailingListValidated ?
                                    <>
                                        <Button bsStyle='primary' style={{ marginRight: 10 }} disabled={!isValid} onClick={() => this.handleSubmit(true)}>Confirm &amp; Continue</Button>
                                        <Button disabled={!isValid} onClick={() => this.handleSubmit(false)}>Confirm Only</Button>
                                        <Button bsStyle='link' onClick={this.handleCancel}>Cancel &amp; Return to the Campaign List</Button>
                                    </>
                                    :
                                    <Button bsStyle='link' onClick={this.handleCancel}>Return to the Campaign List</Button>
                                }
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getCampaign: (id) => { return dispatch(MicrositeActionCreators.getCampaign(id, true)); },
        getValidatedCampaignMailingList: (id) => { return dispatch(MicrositeActionCreators.getValidatedCampaignMailingList(id)); },
        confirmMailingList: (id) => { return dispatch(MicrositeActionCreators.confirmMailingList(id)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ConfirmMailingList);